/**
 * Candid Upload tab on the single-order layout.
 *
 * Opens the Candid RPA status modal from a table action-icon click,
 * using the write-back `use_case` to pick the tab.
 */
import { useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import CustomTable from "../../../../components/custom-table/custom-table";
import { useDataAccumulatorForSingleOrder } from "../../../../context/dataAccumulatorForSingleOrder";
import { ModalId } from "../../../../enums/modalId";
import { setOpenedModalId } from "../../../../redux/slice/modalSlice";
import { AppDispatch } from "../../../../redux/store/store";
import { CandidUploadTableHeaders } from "./candidUploadTable";
import "./candidUploadTable.scss";
import CandidUploadTriggerMenu from "./candidUploadTriggerMenu";
import { useCandidUploadActions } from "./hooks/useCandidUploadActions";
import NoCandidDataFound from "./noCandidDataFound";
import { CandidWritebackRecord } from "./types/candidWriteback";
import { groupCandidWritebacksByUseCase } from "./utils/groupCandidWritebacksByUseCase";
import { mapCandidUseCaseToNavItem } from "./utils/mapCandidUseCaseToNavItem";
import { mapCandidWritebacksToTableData } from "./utils/mapCandidWritebacksToTableData";
import { toCandidWritebackRecords } from "./utils/toCandidWritebackRecord";

/**
 * Renders the Candid Upload tab, including the RPA status entry point.
 *
 * @returns The Candid Upload tab content
 */
const CandidUpload = () => {
  const { candidHistoryWriteback, candidHistoryWritebackLoading } =
    useDataAccumulatorForSingleOrder();
  const writebackRecords = useMemo(
    () => toCandidWritebackRecords(candidHistoryWriteback?.attempts),
    [candidHistoryWriteback],
  );
  const tableData = useMemo(
    () => mapCandidWritebacksToTableData(writebackRecords),
    [writebackRecords],
  );
  const hasData = writebackRecords.length > 0;
  const showTable = candidHistoryWritebackLoading || hasData;
  const { handleDropdownActionClick } = useCandidUploadActions();
  const dispatch = useDispatch<AppDispatch>();

  /**
   * Opens the Candid RPA status modal on the tab that matches the row's
   * write-back `use_case`. Passes grouped history rows and `rowData` so the
   * Auth Letter sidenav can open on that row's `created_at` date.
   *
   * `IconCell` already forwards `value.rowData` (the
   * {@link CandidWritebackRecord}), not the wrapped cell value.
   *
   * @param record - Write-back row attached to the action icon
   * @returns void
   */
  const handleViewIconClick = useCallback(
    (record: CandidWritebackRecord) => {
      if (!record?.use_case) {
        return;
      }

      dispatch(
        setOpenedModalId({
          id: ModalId.CANDID_RPA_STATUS_MODAL,
          metaData: {
            selectedNavItem: mapCandidUseCaseToNavItem(record.use_case),
            selectedItem: record.id,
            groupedByUseCase: groupCandidWritebacksByUseCase(writebackRecords),
            rowData: record,
          },
        }),
      );
    },
    [dispatch, writebackRecords],
  );

  return (
    <div className="candid-upload--container flex h-full overflow-y-auto">
      <div className="candid-upload--table w-full">
        {showTable ? (
          <CustomTable
            tableHeaders={CandidUploadTableHeaders}
            tableData={tableData}
            itemsPerPage={tableData.length}
            pagesPerView={5}
            count={tableData.length}
            tableName="candidUpload"
            hideSearchBar
            hidePagination
            disableHoverAndClick
            showRefreshButton
            isFetching={candidHistoryWritebackLoading}
            headerChildren={
              <CandidUploadTriggerMenu onAction={handleDropdownActionClick} />
            }
            onIconCellClick={handleViewIconClick}
            onRowClick={handleViewIconClick}
          />
        ) : (
          <NoCandidDataFound />
        )}
      </div>
    </div>
  );
};

export default CandidUpload;
